<!--
  The one window picker. Used by Host.svelte (full page) and SwitchWindow.svelte (popover).
  Owns: window list, thumbnails, OS change events, search. Nothing else.
  Everything below is lifted unchanged from Host.svelte; only the props are new.
  CSS: reuse the existing .picker__* and .pcard* classes as-is.
-->
<script lang="ts">
  import { untrack, type Snippet } from 'svelte'
  import {
    listWindows,
    windowThumbnail,
    watchWindows,
    onWindowsChanged,
    type WindowInfo,
  } from './tauri'
  import { t } from './i18n.svelte'

  let {
    active,
    selectedHwnd = null,
    windows = $bindable([]),
    thumbs = $bindable({}),
    onPick,
    playing = () => false,
    head,
  }: {
    /** Thumbnails and OS hooks only run while true (replaces `pickerActive`). */
    active: boolean
    selectedHwnd?: number | null
    /** The current list, for callers that need to look a window up. */
    windows?: WindowInfo[]
    /** Thumbnail object URLs by hwnd. */
    thumbs?: Record<number, string>
    onPick: (w: WindowInfo) => void
    /** Is this process playing sound? Those windows sort first. */
    playing?: (pid: number) => boolean
    /** Optional left side of the header row, e.g. the page title. */
    head?: Snippet
  } = $props()

  let query = $state('')
  let loading = $state(false)

  const shown = $derived(
    windows
      .filter((w) => {
        const q = query.trim().toLowerCase()
        return q === '' || w.title.toLowerCase().includes(q) || w.exe.toLowerCase().includes(q)
      })
      .sort((a, b) => Number(playing(b.pid)) - Number(playing(a.pid))),
  )

  function clearThumbs() {
    for (const url of Object.values(untrack(() => thumbs))) URL.revokeObjectURL(url)
    thumbs = {}
  }

  const key = (w: WindowInfo) =>
    `${w.hwnd}|${w.title}|${w.width}x${w.height}|${w.is_minimized ? 1 : 0}`

  // Quiet re-list: keeps thumbnails of surviving windows, assigns nothing if unchanged.
  let syncing = false
  let again = false
  async function sync() {
    if (syncing) return void (again = true)
    syncing = true
    try {
      do {
        again = false
        const found = await listWindows()
        if (!untrack(() => active)) return
        const before = untrack(() => windows)
        if (found.length === before.length && found.every((w, i) => key(w) === key(before[i])))
          continue
        const alive = new Set(found.map((w) => w.hwnd))
        for (const [hwnd, url] of Object.entries(untrack(() => thumbs))) {
          if (alive.has(Number(hwnd))) continue
          URL.revokeObjectURL(url)
          delete thumbs[Number(hwnd)]
        }
        windows = found
      } while (again)
    } catch {
      // Convenience only; the refresh button reports errors.
    } finally {
      syncing = false
    }
  }

  async function refresh() {
    loading = true
    clearThumbs()
    try {
      windows = await listWindows()
    } finally {
      loading = false
    }
  }

  // Thumbnails, one at a time so the grid fills in gradually.
  $effect(() => {
    if (!active) return
    const queue = windows.filter((w) => !w.is_minimized)
    let cancelled = false
    void (async () => {
      for (const w of queue) {
        if (cancelled) return
        if (untrack(() => thumbs[w.hwnd])) continue
        try {
          const url = await windowThumbnail(w.hwnd)
          if (cancelled) return void URL.revokeObjectURL(url)
          thumbs[w.hwnd] = url
        } catch {
          // Keeps its placeholder.
        }
      }
    })()
    return () => (cancelled = true)
  })

  // OS hooks + first load, only while visible; torn down when not.
  $effect(() => {
    if (!active) return
    let off: (() => void) | undefined
    let stopped = false
    void watchWindows(true).catch(() => {})
    void onWindowsChanged(() => void sync()).then((u) => (stopped ? u() : (off = u)))
    const onFocus = () => void sync()
    window.addEventListener('focus', onFocus)
    void refresh()
    return () => {
      stopped = true
      off?.()
      window.removeEventListener('focus', onFocus)
      void watchWindows(false).catch(() => {})
      clearThumbs()
    }
  })
</script>

<div class="spread picker__head" style="padding: 14px 0 6px; gap: 20px">
  {@render head?.()}
  <label class="picker__search">
    <svg viewBox="0 -960 960 960" width="18" height="18" fill="currentColor" aria-hidden="true"
      ><path
        d="M378-329q-108.16 0-183.08-75Q120-479 120-585t75-181q75-75 181.5-75t181 75Q632-691 632-584.85 632-542 618-502q-14 40-42 75l242 240q9 8.56 9 21.78T818-143q-9 9-22.22 9-13.22 0-21.78-9L533-384q-30 26-69.96 40.5Q423.08-329 378-329Zm-1-60q81.25 0 138.13-57.5Q572-504 572-585t-56.87-138.5Q458.25-781 377-781q-82.08 0-139.54 57.5Q180-666 180-585t57.46 138.5Q294.92-389 377-389Z"
      /></svg
    >
    <input bind:value={query} placeholder={t('Find a window')} aria-label={t('Find a window')} />
    <button
      type="button"
      class="picker__refresh"
      aria-label={t('Refresh windows')}
      onclick={refresh}
      disabled={loading}>↻</button
    >
  </label>
</div>

{#if windows.length === 0}
  <div class="notice">
    {t('No shareable windows found. Open the window you want to share, then refresh.')}
  </div>
{:else}
  <div class="picker__grid">
    {#each shown as window (window.hwnd)}
      <button
        type="button"
        class="pcard"
        class:pcard--min={window.is_minimized}
        aria-pressed={selectedHwnd === window.hwnd}
        title={`${window.width}×${window.height}`}
        onclick={() => onPick(window)}
      >
        <span class="pcard__thumb">
          {#if thumbs[window.hwnd]}
            <img class="pcard__img" src={thumbs[window.hwnd]} alt="" />
            {#if window.is_minimized}
              <span class="pcard__min pcard__min--over">
                {window.exe}
                <small>{t('Minimized')}</small>
              </span>
            {/if}
          {:else}
            <span class="pcard__min">
              {window.exe}
              {#if window.is_minimized}<small>{t('Minimized')}</small>{/if}
            </span>
          {/if}
          {#if playing(window.pid)}<span class="pcard__sound">{t('Sound')}</span>{/if}
          {#if selectedHwnd === window.hwnd}<span class="pcard__check">✓</span>{/if}
        </span>
        <span class="pcard__title">{window.title || window.exe}</span>
      </button>
    {:else}
      <div class="muted">{t('No window matches “{query}”.', { query })}</div>
    {/each}
  </div>
{/if}
